import { Button } from "@datapay-by-datacom/design-system";
import type { NormalisationRunSuccess } from "../types/normalisationRun";
import s from "./NormalisationCompletePanel.module.css";

export interface NormalisationCompletePanelProps {
  result: NormalisationRunSuccess;
  /** Import source type label from the catalogue selection (CN-02 / CN-06). */
  importSourceType: string;
  onUploadAnother: () => void;
  onDownloadComponent: () => void;
  onDownloadGross: () => void;
  onDownloadSuper: () => void;
  onDownloadIssues?: () => void;
  onDownloadBundle: () => void;
  isDownloading?: boolean;
}

function formatWarningCount(count: number): string {
  if (count === 0) {
    return "0 warnings";
  }
  const label = count === 1 ? "warning" : "warnings";
  return `${count} ${label}`;
}

function formatSubtitle(result: NormalisationRunSuccess): string {
  if (result.outcome === "partial") {
    const failurePart =
      result.payslipBlocksFailed > 0
        ? ` · ${result.payslipBlocksFailed} per-slip ${
            result.payslipBlocksFailed === 1 ? "failure" : "failures"
          }`
        : "";
    return `${result.payslipBlocksParsed} of ${result.payslipBlocksTotal} payslip blocks parsed${failurePart} · ${formatWarningCount(result.warningCount)} · 0 run-blocking issues`;
  }

  return `${result.payslipBlocksTotal} payslip blocks parsed · 0 run-blocking issues · ${formatWarningCount(result.warningCount)}`;
}

function formatPayslipBlockIndex(index: number | undefined): string {
  return index === undefined ? "—" : String(index);
}

function formatPreviewText(value: string): string {
  return value.trim() === "" ? "—" : value;
}

function formatPerSlipWarning(result: NormalisationRunSuccess): string {
  const slipLabel = result.payslipBlocksFailed === 1 ? "payslip" : "payslips";
  return `${result.payslipBlocksFailed} ${slipLabel} could not be read — Summary Totals include ${result.employeeSummaryRows} employees only. See issues export.`;
}

function formatRunWarningsBanner(result: NormalisationRunSuccess): string {
  const label = result.warningCount === 1 ? "warning" : "warnings";
  return `${result.warningCount} ${label} recorded — see issues export for details.`;
}

/** CN-06 — complete and non-run-blocking partial outcomes share one screen. */
export function NormalisationCompletePanel({
  result,
  importSourceType,
  onUploadAnother,
  onDownloadComponent,
  onDownloadGross,
  onDownloadSuper,
  onDownloadIssues,
  onDownloadBundle,
  isDownloading = false,
}: NormalisationCompletePanelProps) {
  const hasSuperArtifact = result.availableArtifacts.includes("super.csv");
  const isPartial = result.outcome === "partial";
  const previewRows = result.detailPreview;
  const previewRowCount = previewRows.length;
  const showingTruncatedPreview =
    previewRowCount > 0 && previewRowCount < result.employeeSummaryRows;
  const issueCount = result.issues?.length ?? 0;
  const showPerSlipWarning = isPartial && result.payslipBlocksFailed > 0;
  const showRunWarningsBanner =
    isPartial && result.warningCount > 0 && result.payslipBlocksFailed === 0;
  const hasIssuesExport = issueCount > 0 && onDownloadIssues !== undefined;
  const { summaryTotals: totals } = result;

  return (
    <section
      className={s.section}
      aria-labelledby="normalisation-complete-heading"
    >
      <h2 id="normalisation-complete-heading" className={s.heading}>
        Normalisation complete
      </h2>
      <p className={s.subtitle}>{formatSubtitle(result)}</p>

      <div className={s.successBanner} role="status">
        Gross and Component CSV ready — no Datapay writes performed.
      </div>

      {showPerSlipWarning && (
        <div className={s.warningBanner} role="status">
          {formatPerSlipWarning(result)}
        </div>
      )}

      {showRunWarningsBanner && (
        <div className={s.warningBanner} role="status">
          {formatRunWarningsBanner(result)}
        </div>
      )}

      <div className={s.runSummaryCard}>
        <h3 className={s.runSummaryTitle}>Normalisation summary (run)</h3>
        <table className={s.runSummaryTable}>
          <tbody>
            <tr>
              <th scope="row">Import source type</th>
              <td>{importSourceType}</td>
            </tr>
            <tr>
              <th scope="row">Summary rows</th>
              <td>{result.employeeSummaryRows}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        className={s.tableScroll}
        tabIndex={0}
        aria-label="Employee summary preview"
      >
        <table className={s.previewTable}>
          <caption className={s.caption}>
            Summary preview from the normalisation run
          </caption>
          <colgroup>
            <col className={s.previewColBlock} />
            <col className={s.previewColEmployee} />
            <col className={s.previewColAmount} />
            <col className={s.previewColAmount} />
            <col className={s.previewColAmount} />
            <col className={s.previewColAmount} />
            <col className={s.previewColAmount} />
            <col className={s.previewColAmount} />
            <col className={s.previewColAmount} />
            <col className={s.previewColOtherSuper} />
            <col className={s.previewColOtherSuper} />
            <col className={s.previewColAmount} />
          </colgroup>
          <thead>
            <tr>
              <th scope="col">Payslip No.</th>
              <th scope="col">Employee Code</th>
              <th scope="col">Gross Pay</th>
              <th scope="col">Gross Taxable</th>
              <th scope="col">PAYE</th>
              <th scope="col">Net Pay</th>
              <th scope="col">KS EE</th>
              <th scope="col">KS ER Gross/Net</th>
              <th scope="col">KS ER ESCT</th>
              <th scope="col">Other Super EE</th>
              <th scope="col">Other Super ER</th>
              <th scope="col">Student Loan</th>
            </tr>
          </thead>
          <tbody>
            {previewRows.map((row, index) => (
              <tr key={`${row.employeeCode}-${index}`}>
                <td>{formatPayslipBlockIndex(row.payslipBlockIndex)}</td>
                <td>{row.employeeCode}</td>
                <td>{formatPreviewText(row.grossPay)}</td>
                <td>{row.gross}</td>
                <td>{row.tax}</td>
                <td>{row.nettPay}</td>
                <td>{row.ksEe}</td>
                <td>{row.ksEr}</td>
                <td>{row.esct}</td>
                <td>{formatPreviewText(row.otherSuperEe)}</td>
                <td>{formatPreviewText(row.otherSuperEr)}</td>
                <td>{formatPreviewText(row.studentLoan)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className={s.previewTotalsRow}>
              <th colSpan={2} scope="row" className={s.previewStickyTotalsLabel}>
                <strong>Totals</strong>
              </th>
              <td className={s.previewTotalsPlaceholder} aria-hidden />
              <td>{totals.gross}</td>
              <td>{totals.tax}</td>
              <td>{totals.nettPay}</td>
              <td>{totals.ksEe}</td>
              <td>{totals.ksEr}</td>
              <td>{totals.esct}</td>
              <td>{formatPreviewText(totals.otherSuperEe)}</td>
              <td>{formatPreviewText(totals.otherSuperEr)}</td>
              <td>{formatPreviewText(totals.studentLoan)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <p className={s.previewFootnote}>
        {showingTruncatedPreview
          ? `Showing ${previewRowCount} of ${result.employeeSummaryRows} summary rows in preview. `
          : ""}
        Summary preview only — amounts unsigned as printed. No employee name,
        IRD, or bank columns.
      </p>

      <div className={s.actions}>
        <Button
          variant="primary"
          onPress={onDownloadComponent}
          isDisabled={isDownloading}
        >
          Download Component CSV
        </Button>
        <Button
          variant="primary"
          onPress={onDownloadGross}
          isDisabled={isDownloading}
        >
          Download Gross CSV
        </Button>
        <Button
          variant="primary"
          onPress={onDownloadSuper}
          isDisabled={isDownloading || !hasSuperArtifact}
        >
          Download Super CSV
        </Button>
        <Button
          variant="secondary"
          onPress={onDownloadBundle}
          isDisabled={isDownloading}
        >
          Download all extracts (zip)
        </Button>
        <Button
          variant="secondary"
          onPress={onDownloadIssues}
          isDisabled={isDownloading || !hasIssuesExport}
        >
          Download issues export
        </Button>
        <Button variant="secondary" onPress={onUploadAnother}>
          Upload another file
        </Button>
      </div>
    </section>
  );
}
